import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type {
  ModalFilter,
  RangeInfo,
  SpendingTransaction,
  TimeFrameKey,
} from './types';

dayjs.extend(utc);

/**
 * Shared tooltip plugin config for spending charts. Used with the external
 * tooltip renderer so Chart.js computes default placement and box size.
 *
 * NOTE — hex literals mirror Chart.js string colour input (Figma 357:24567).
 */
export const SHARED_TOOLTIP_PLUGIN_CONFIG = {
  backgroundColor: '#15181d',
  bodyColor: '#ffffff',
  titleColor: '#ffffff',
  footerColor: '#ffffff',
  padding: { x: 20, y: 9 },
  displayColors: false,
  cornerRadius: 12,
  caretSize: 8,
  titleAlign: 'center' as const,
  bodyAlign: 'center' as const,
  footerAlign: 'center' as const,
  titleFont: { size: 12, weight: 'bold' as const },
  bodyFont: { size: 12, weight: 'normal' as const },
  footerFont: { size: 14, weight: 'normal' as const },
  footerMarginTop: 4,
};

/**
 * Format a numeric dollar amount as US currency with two decimals.
 * Null / undefined / 0 → "$0".
 */
export function formatUSD(amount: number | null | undefined): string {
  if (!amount) return '$0';
  return amount.toLocaleString('en-us', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

function buildDayBuckets(count: number, anchor: dayjs.Dayjs): string[] {
  // Chronological, oldest → newest, so index N-1 is the most recent bar.
  const list: string[] = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    list.push(anchor.subtract(i, 'day').startOf('day').toISOString());
  }
  return list;
}

function buildMonthBuckets(count: number, anchor: dayjs.Dayjs): string[] {
  const list: string[] = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    list.push(anchor.subtract(i, 'month').startOf('month').toISOString());
  }
  return list;
}

/**
 * Given a TimeFrameKey, compute the derived date range in the shape the slice /
 * selectors need. Mirrors Angular's SpendingService.getStartAndEndDate + getChartLabel,
 * rebuilt around UTC-consistent dayjs to fix the Angular local/UTC date-match bug.
 *
 * All state written to Redux is serialisable — compDateISOs are ISO strings.
 */
export function computeRangeInfo(range: TimeFrameKey): RangeInfo {
  const nowUTC = dayjs.utc();
  const fmt = (d: dayjs.Dayjs) => d.format('MM-DD-YYYY');

  switch (range) {
    case 'last7days': {
      const start = nowUTC.subtract(6, 'day').startOf('day');
      return {
        startDate: fmt(start),
        endDate: fmt(nowUTC),
        compDateISOs: buildDayBuckets(7, nowUTC),
        isMonthBucket: false,
      };
    }
    case 'last14days': {
      const start = nowUTC.subtract(13, 'day').startOf('day');
      return {
        startDate: fmt(start),
        endDate: fmt(nowUTC),
        compDateISOs: buildDayBuckets(14, nowUTC),
        isMonthBucket: false,
      };
    }
    case 'thisMonth': {
      const start = nowUTC.startOf('month');
      const dayCount = Math.max(1, nowUTC.date());
      return {
        startDate: fmt(start),
        endDate: fmt(nowUTC),
        compDateISOs: buildDayBuckets(dayCount, nowUTC),
        isMonthBucket: false,
      };
    }
    case 'lastMonth': {
      const lastMonthEnd = nowUTC.subtract(1, 'month').endOf('month');
      const lastMonthStart = nowUTC.subtract(1, 'month').startOf('month');
      const dayCount = lastMonthEnd.date();
      return {
        startDate: fmt(lastMonthStart),
        endDate: fmt(lastMonthEnd),
        compDateISOs: buildDayBuckets(dayCount, lastMonthEnd),
        isMonthBucket: false,
      };
    }
    case 'last3months': {
      const start = nowUTC.subtract(2, 'month').startOf('month');
      return {
        startDate: fmt(start),
        endDate: fmt(nowUTC),
        compDateISOs: buildMonthBuckets(3, nowUTC),
        isMonthBucket: true,
      };
    }
    case 'last6months': {
      const start = nowUTC.subtract(5, 'month').startOf('month');
      return {
        startDate: fmt(start),
        endDate: fmt(nowUTC),
        compDateISOs: buildMonthBuckets(6, nowUTC),
        isMonthBucket: true,
      };
    }
    case 'last12months': {
      const start = nowUTC.subtract(11, 'month').startOf('month');
      return {
        startDate: fmt(start),
        endDate: fmt(nowUTC),
        compDateISOs: buildMonthBuckets(12, nowUTC),
        isMonthBucket: true,
      };
    }
    case 'thisYear': {
      const start = nowUTC.startOf('year');
      const monthCount = Math.max(1, nowUTC.month() + 1);
      return {
        startDate: fmt(start),
        endDate: fmt(nowUTC),
        compDateISOs: buildMonthBuckets(monthCount, nowUTC),
        isMonthBucket: true,
      };
    }
    case 'lastYear': {
      const lastYearStart = nowUTC.subtract(1, 'year').startOf('year');
      const lastYearEnd = nowUTC.subtract(1, 'year').endOf('year');
      return {
        startDate: fmt(lastYearStart),
        endDate: fmt(lastYearEnd),
        compDateISOs: buildMonthBuckets(12, lastYearEnd),
        isMonthBucket: true,
      };
    }
    default: {
      // Exhaustiveness guard — should never fire.
      const start = nowUTC.startOf('month');
      return {
        startDate: fmt(start),
        endDate: fmt(nowUTC),
        compDateISOs: buildDayBuckets(Math.max(1, nowUTC.date()), nowUTC),
        isMonthBucket: false,
      };
    }
  }
}

/**
 * Pure client-side filter for the details modal.
 * NEVER parses the display label — filter type + compDateISO + isMonthBucket
 * are all passed explicitly by the caller.
 */
export function filterModalTransactions(
  transactions: SpendingTransaction[],
  filter: ModalFilter,
): SpendingTransaction[] {
  if (filter.type === 'category') {
    return transactions.filter((t) => t.category === filter.label);
  }
  if (!filter.compDateISO) return [];
  const bucketUTC = dayjs.utc(filter.compDateISO);
  if (filter.isMonthBucket) {
    return transactions.filter((t) => {
      const tUTC = dayjs.utc(t.date);
      return (
        tUTC.month() === bucketUTC.month() && tUTC.year() === bucketUTC.year()
      );
    });
  }
  return transactions.filter((t) => {
    const tUTC = dayjs.utc(t.date);
    return (
      tUTC.date() === bucketUTC.date() &&
      tUTC.month() === bucketUTC.month() &&
      tUTC.year() === bucketUTC.year()
    );
  });
}
